import {
  ReactElement,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

export interface ResizablePanelProps {
  children: ReactNode;
  title: string;
  position?: "left" | "right";
  minWidth?: number;
  maxWidth?: number;
  defaultWidth?: number;
  collapsedRailWidth?: number;
  storageKey?: string;
  className?: string;
}

const ResizablePanel = ({
  children,
  title,
  position = "left",
  minWidth = 200,
  maxWidth = 600,
  defaultWidth = 300,
  collapsedRailWidth = 28,
  storageKey = "resizablePanel",
  className = "",
}: ResizablePanelProps): ReactElement => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartWidth, setDragStartWidth] = useState(0);
  const [width, setWidth] = useState<number>(() => {
    const stored =
      typeof window !== "undefined"
        ? window.sessionStorage.getItem(`${storageKey}Width`)
        : null;
    const parsed = stored ? parseInt(stored, 10) : defaultWidth;
    if (Number.isNaN(parsed)) return defaultWidth;
    return Math.min(Math.max(parsed, minWidth), maxWidth);
  });
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored =
      typeof window !== "undefined"
        ? window.sessionStorage.getItem(`${storageKey}Collapsed`)
        : null;
    return stored === "true";
  });

  // Persist width & collapsed state
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(`${storageKey}Width`, String(width));
    }
  }, [width, storageKey]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(`${storageKey}Collapsed`, String(collapsed));
    }
  }, [collapsed, storageKey]);

  // Resizing handlers
  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = event.clientX - dragStartX;
      const newWidth =
        position === "left"
          ? Math.min(Math.max(dragStartWidth + deltaX, minWidth), maxWidth)
          : Math.min(Math.max(dragStartWidth - deltaX, minWidth), maxWidth);
      setWidth(newWidth);
    },
    [isDragging, position, minWidth, maxWidth, dragStartX, dragStartWidth],
  );

  const onMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
  }, [isDragging]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    } else {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isDragging, onMouseMove, onMouseUp]);

  const handleResizerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setDragStartX(e.clientX);
    setDragStartWidth(width);
    setIsDragging(true);
  };

  const handleResizerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setWidth((w) => Math.max(minWidth, w - 10));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setWidth((w) => Math.min(maxWidth, w + 10));
    }
  };

  const handleCollapsedRailMouseDown = (e: React.MouseEvent) => {
    // Expand immediately and start dragging so users can drag to desired width
    setCollapsed(false);
    setDragStartX(e.clientX);
    setDragStartWidth(defaultWidth);
    setIsDragging(true);
  };

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  const borderClass = position === "left" ? "border-r" : "border-l";
  const chevronIcon =
    position === "left" ? <ChevronLeftIcon /> : <ChevronRightIcon />;
  const oppositeChevronIcon =
    position === "left" ? <ChevronRightIcon /> : <ChevronLeftIcon />;
  const accentPosition = position === "left" ? "left-0" : "right-0";

  return (
    <div className={`relative h-full flex ${className}`}>
      {/* Collapsed rail */}
      {collapsed ? (
        <div
          className={`h-full flex items-center justify-center bg-white ${borderClass} border-gray-200 shadow-sm cursor-col-resize group`}
          style={{
            width: collapsedRailWidth,
            position: "relative",
            zIndex: 10,
          }}
          onMouseDown={handleCollapsedRailMouseDown}
          title={`Expand and resize ${title.toLowerCase()}`}
          aria-label={`Collapsed ${title.toLowerCase()} panel`}
        >
          {/* Visual colored marker */}
          <div
            className={`absolute ${accentPosition} top-0 h-full w-1 bg-[#19C1DB]`}
          />
          <Button
            aria-label={`Expand ${title.toLowerCase()} panel`}
            title={`Expand ${title.toLowerCase()}`}
            onClick={toggleCollapsed}
            className="min-w-0 p-1 text-[#19C1DB]"
          >
            {oppositeChevronIcon}
          </Button>
        </div>
      ) : (
        <Box
          className={`bg-white ${borderClass} border-gray-200 flex flex-col h-full`}
          style={{ width }}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
            <Typography variant="subtitle2" className="text-gray-700">
              {title}
            </Typography>
            <Button
              aria-label={`Collapse ${title.toLowerCase()} panel`}
              title={`Collapse ${title.toLowerCase()}`}
              onClick={toggleCollapsed}
              className="min-w-0 p-1"
            >
              {chevronIcon}
            </Button>
          </div>
          <div className="flex-1 overflow-hidden">{children}</div>
        </Box>
      )}

      {/* Resizer handle (always present for discoverability, works while collapsed) */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${title.toLowerCase()} panel`}
        tabIndex={0}
        onKeyDown={handleResizerKeyDown}
        onMouseDown={handleResizerMouseDown}
        className={`h-full w-2 cursor-col-resize bg-transparent hover:bg-gray-300 active:bg-gray-400 transition-colors duration-150`}
        title="Drag to resize"
        style={{
          background:
            "linear-gradient(to right, transparent 0%, transparent 50%, rgba(156, 163, 175, 0.3) 50%, rgba(156, 163, 175, 0.3) 100%)",
        }}
      />
    </div>
  );
};

export default ResizablePanel;
